import { HostNarrationProvider } from '../narration/HostNarration.tsx';
/**
 * `/s/:sessionId`: loads credentials, opens the session client, and renders the surface that matches
 * `view.session.status` (docs/ARCHITECTURE.md §3, §12). `SessionSurface` is reused by `/display/:sessionId`
 * (DisplayPage.tsx) once it has spectator credentials, so the two routes share every bit of live-session
 * behaviour and differ only in how credentials are obtained.
 */
import { useEffect, useMemo } from 'react';
import { deriveWsUrl } from '../api.ts';
import { clearCredentials, HOST_SESSION_KEY, loadCredentials, type StoredCredentials } from './credentials.ts';
import { useSessionClient } from './useSessionClient.ts';
import { SessionContext, useSessionContext, type SessionContextValue } from './SessionContext.ts';
import type { ClientStatus, ClosedReason } from './client.ts';
import { useReducedMotion } from '../shell/useReducedMotion.ts';
import { Link, useSearchParams } from '../shell/router.tsx';
import { HostPreparation } from './HostPreparation.tsx';
import { Lobby } from '../lobby/Lobby.tsx';
import { PlaySurface } from './PlaySurface.tsx';
import { Results } from './Results.tsx';
import { strings } from '../shell/strings.ts';
import { useVoice } from '../voice/useVoice.ts';
import { VoicePanel } from '../voice/VoicePanel.tsx';
import { GameAudioProvider } from '../audio/GameAudio.tsx';

export interface SessionPageProps {
  sessionId: string;
}

/** `/s/:sessionId` — normal participant entry point. */
export function SessionPage({ sessionId }: SessionPageProps) {
  const credentials = loadCredentials(sessionId);
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get('invite');

  if (!credentials) {
    return (
      <div className="page stack">
        <p>{strings.session.noCredentials}</p>
        {inviteToken ? (
          <p>
            <Link to={`/join/${inviteToken}`} className="button button-primary">
              {strings.session.joinLink}
            </Link>
          </p>
        ) : null}
      </div>
    );
  }

  return <SessionSurface sessionId={sessionId} credentials={credentials} />;
}

export interface SessionSurfaceProps {
  sessionId: string;
  credentials: StoredCredentials;
  /** Sent as the hello frame's `client.kind`; does not itself change permissions (the server derives those
   *  from the authenticated participant's role). Use for `/display/:sessionId`. */
  forceReadOnly?: boolean;
}

/** The live session view: connects, then renders Lobby/PlaySurface/Results by session status. */
export function SessionSurface({ sessionId, credentials, forceReadOnly }: SessionSurfaceProps) {
  const wsUrl = useMemo(() => deriveWsUrl(), []);
  const clientKind: 'display' | 'player' = forceReadOnly ? 'display' : 'player';
  const clientOptions = useMemo(
    () => ({ sessionId, token: credentials.token, wsUrl, clientKind }),
    [sessionId, credentials.token, wsUrl, clientKind],
  );
  const { client, snapshot } = useSessionClient(clientOptions);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!credentials.isHost) return;
    try { localStorage.setItem(HOST_SESSION_KEY, sessionId); } catch { /* Browser storage may be unavailable. */ }
  }, [credentials.isHost, sessionId]);

  useEffect(() => {
    const reason = snapshot.closed?.reason;
    if (reason === 'left' || reason === 'removed' || reason === 'unauthenticated' || reason === 'expired') {
      clearCredentials(sessionId, credentials.token);
    }
  }, [sessionId, snapshot.closed?.reason]);

  if (snapshot.closed) {
    return <ClosedPage reason={snapshot.closed.reason} message={snapshot.closed.message} />;
  }

  if (!snapshot.view) {
    return (
      <div className="page">
        <p role="status" aria-live="polite">
          {strings.session.loading}
        </p>
      </div>
    );
  }

  const contextValue: SessionContextValue = {
    sessionId,
    client,
    view: snapshot.view,
    self: snapshot.self,
    status: snapshot.status,
    serverTimeOffset: snapshot.serverTimeOffset,
    closed: snapshot.closed,
    credentials,
    reducedMotion,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      <HostNarrationProvider><GameAudioProvider><SessionSurfaceBody connectionStatus={snapshot.status} /></GameAudioProvider></HostNarrationProvider>
    </SessionContext.Provider>
  );
}

/**
 * Split out so `useVoice()` (T6) can call `useSessionContext()`: it needs to run inside the provider above,
 * not in `SessionSurface` where the context value is only being constructed. Calling it once here — rather
 * than inside `Lobby`/`PlaySurface` themselves — is what keeps the voice connection alive across the
 * lobby/board/activity transitions those two swap between (docs/ARCHITECTURE.md §9).
 */
function SessionSurfaceBody({ connectionStatus }: { connectionStatus: ClientStatus }) {
  const { view, self, sessionId, credentials } = useSessionContext();
  const voice = useVoice();
  const status = view.session.status;
  const search = useSearchParams();
  const preparing = search.get("setup") === "1" && self?.isHost;

  return (
    <div className="page stack">
      <ConnectionStatusBanner status={connectionStatus} />
      {preparing ? <HostPreparation /> : status === 'lobby' ? (
        <Lobby voice={voice}>
          <VoicePanel voice={voice} compact />
        </Lobby>
      ) : status === 'running' ? (
        <PlaySurface voice={<VoicePanel voice={voice} compact />} />
      ) : (
        <><Results /><VoicePanel voice={voice} compact /></>
      )}
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}

function ConnectionStatusBanner({ status }: { status: ClientStatus }) {
  if (status === 'connected') return null;
  const text =
    status === 'connecting'
      ? strings.connection.connecting
      : status === 'reconnecting'
        ? strings.connection.reconnecting
        : strings.connection.closed;
  return (
    <p className="badge badge-danger" role="status" aria-live="polite">
      {text}
    </p>
  );
}

const CLOSED_REASON_TEXT: Record<ClosedReason, string> = {
  superseded: strings.connection.closedSuperseded,
  removed: strings.connection.closedRemoved,
  left: strings.connection.closedLeft,
  session_ended: strings.connection.closedSessionEnded,
  unauthenticated: strings.connection.closedUnauthenticated,
  expired: strings.connection.closedExpired,
  server_shutdown: strings.connection.closedServerShutdown,
};

function ClosedPage({ reason, message }: { reason: ClosedReason; message: string }) {
  return (
    <div className="page">
      <div className="banner banner-danger stack-sm" role="alert">
        <p>{CLOSED_REASON_TEXT[reason]}</p>
        <p className="text-muted">{message}</p>
        {reason === 'left' || reason === 'removed' || reason === 'unauthenticated' || reason === 'expired' ? (
          <p><Link to="/momentum" className="button">Return to Momentum</Link></p>
        ) : null}
      </div>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
